// Mock responses for AI Chat (used when ai_chat_mode = "mock")
// These responses simulate the AI assistant for testing purposes

import type { MockResponse } from '../types';

// Emergency keywords that should trigger immediate doctor referral
export const EMERGENCY_KEYWORDS_DE = [
  'blutung',
  'starke blutung',
  'starke schmerzen',
  'keine bewegung',
  'baby bewegt sich nicht',
  'fruchtwasser',
  'blasensprung',
  'wehen',
  'fieber',
  'ohnmacht',
  'schwindel stark',
  'sehstörung',
  'kopfschmerzen stark',
];

export const EMERGENCY_KEYWORDS_EN = [
  'bleeding',
  'heavy bleeding',
  'severe pain',
  'no movement',
  'baby not moving',
  'amniotic fluid',
  'water broke',
  'contractions',
  'fever',
  'fainting',
  'severe dizziness',
  'vision problems',
  'severe headache',
];

// Keep old export for backwards compatibility
export const EMERGENCY_KEYWORDS = EMERGENCY_KEYWORDS_DE;

// Check if message contains emergency keywords
export function containsEmergencyKeyword(message: string): boolean {
  const lowerMessage = message.toLowerCase();
  return [...EMERGENCY_KEYWORDS_DE, ...EMERGENCY_KEYWORDS_EN].some((keyword) =>
    lowerMessage.includes(keyword)
  );
}

// Emergency responses
const EMERGENCY_RESPONSE_DE = `⚠️ **Bitte suche sofort ärztliche Hilfe!**

Die von dir beschriebenen Symptome könnten auf eine ernste Situation hindeuten.

**Kontaktiere jetzt:**
- Deine Frauenärztin/deinen Frauenarzt
- Die Notaufnahme des nächsten Krankenhauses
- Im Notfall: 112

Ich bin ein KI-Assistent und kann keine medizinische Diagnose stellen. Bei den beschriebenen Symptomen ist eine sofortige ärztliche Untersuchung wichtig.`;

const EMERGENCY_RESPONSE_EN = `⚠️ **Please seek medical help immediately!**

The symptoms you described could indicate a serious situation.

**Contact now:**
- Your doctor/midwife
- The nearest hospital emergency room
- In an emergency: 112

I am an AI assistant and cannot make medical diagnoses. With the symptoms described, an immediate medical examination is important.`;

export const EMERGENCY_RESPONSE = EMERGENCY_RESPONSE_DE;

// Mock responses based on keywords - German
const MOCK_RESPONSES_DE: MockResponse[] = [
  {
    keywords: ['übelkeit', 'übel', 'erbrechen', 'morgenübelkeit', 'schlecht'],
    response: `Übelkeit ist besonders im ersten Trimester sehr häufig und betrifft etwa 70-80% aller Schwangeren. Hier einige Tipps, die helfen können:

**Was du versuchen kannst:**
- Kleine, häufige Mahlzeiten statt weniger großer
- Trockene Kekse oder Toast vor dem Aufstehen
- Ingwertee oder Ingwerbonbons
- Frische Luft und Bewegung
- Stark riechende Speisen meiden

**Wann zum Arzt:**
- Bei anhaltendem Erbrechen (mehr als 3x täglich)
- Wenn du nichts bei dir behalten kannst
- Bei Gewichtsverlust oder Dehydrierung

Die gute Nachricht: Bei den meisten Frauen lässt die Übelkeit nach der 12.-14. SSW deutlich nach.`,
  },
  {
    keywords: ['müde', 'müdigkeit', 'erschöpft', 'schlapp', 'energie'],
    response: `Müdigkeit ist eines der häufigsten Symptome in der Schwangerschaft, besonders im ersten und dritten Trimester.

**Warum bist du so müde?**
Dein Körper leistet gerade Unglaubliches! Er bildet die Plazenta, dein Blutvolumen steigt um 50%, und dein Stoffwechsel arbeitet auf Hochtouren.

**Was helfen kann:**
- Gönne dir Ruhepausen, wenn möglich
- Gehe früher ins Bett
- Kurze Power-Naps (20-30 Min.)
- Leichte Bewegung wie Spaziergänge
- Ausreichend Eisen durch Ernährung
- Viel trinken

**Tipp:** Hör auf deinen Körper! Wenn er Ruhe braucht, ist das ein wichtiges Signal.`,
  },
  {
    keywords: ['essen', 'ernährung', 'nahrung', 'lebensmittel', 'darf ich', 'verboten'],
    response: `Eine ausgewogene Ernährung ist in der Schwangerschaft besonders wichtig.

**Empfohlen:**
- Viel Obst und Gemüse (gut waschen!)
- Vollkornprodukte
- Mageres Fleisch (gut durchgegart)
- Fisch 1-2x pro Woche (kein Raubfisch)
- Milchprodukte (pasteurisiert)
- Hülsenfrüchte und Nüsse

**Besser meiden:**
- Rohe tierische Produkte (Sushi, Mett, Tiramisu)
- Rohmilchkäse
- Alkohol
- Zu viel Koffein (max. 200mg/Tag)
- Leber (zu viel Vitamin A)

**Wichtig:** Folsäure und ggf. weitere Nahrungsergänzungsmittel nach Absprache mit deiner Ärztin.

Hast du eine spezifische Frage zu einem Lebensmittel?`,
  },
  {
    keywords: ['sport', 'bewegung', 'training', 'fitness', 'yoga', 'schwimmen'],
    response: `Bewegung in der Schwangerschaft ist super und wird empfohlen!

**Gut geeignet:**
- Schwimmen und Aqua-Fitness
- Yoga (Schwangerschaftsyoga)
- Walken und Spazierengehen
- Leichtes Krafttraining
- Radfahren (vorsichtig, Gleichgewicht!)
- Schwangerschaftsgymnastik

**Besser vermeiden:**
- Kontaktsportarten
- Sportarten mit Sturzgefahr
- Extreme Belastungen
- Übungen in Rückenlage (ab 2. Trimester)
- Tauchen

**Allgemeine Tipps:**
- Höre auf deinen Körper
- Trainiere nie bis zur Erschöpfung
- Halte dich an die "Talk-Test"-Regel
- Trinke ausreichend
- Informiere deine Trainer/in

Wenn du vor der Schwangerschaft aktiv warst, kannst du meist weitermachen. Besprich das am besten mit deiner Ärztin.`,
  },
  {
    keywords: ['schlaf', 'schlafen', 'insomnia', 'einschlafen', 'durchschlafen', 'schlaflos'],
    response: `Schlafprobleme sind in der Schwangerschaft sehr verbreitet, besonders im dritten Trimester.

**Häufige Ursachen:**
- Häufiger Harndrang
- Unbequeme Schlafposition
- Sodbrennen
- Gedankenkarussell
- Babybewegungen

**Was helfen kann:**
- Seitenlage (links ist optimal für die Durchblutung)
- Stillkissen zwischen den Knien
- Abends weniger trinken
- Entspannungsübungen vor dem Schlafen
- Feste Schlafenszeiten
- Kühles, dunkles Schlafzimmer

**Gegen Sodbrennen:**
- Kopf höher lagern
- Letzte Mahlzeit 2-3 Stunden vor dem Schlafen

Kurze Nickerchen tagsüber sind völlig in Ordnung und helfen, das Schlafdefizit auszugleichen.`,
  },
  {
    keywords: ['rücken', 'rückenschmerzen', 'kreuzschmerzen', 'ischias'],
    response: `Rückenschmerzen gehören leider zu den häufigsten Beschwerden in der Schwangerschaft.

**Warum tut der Rücken weh?**
- Gewichtszunahme verlagert den Schwerpunkt
- Hormone lockern Bänder und Gelenke
- Veränderte Körperhaltung

**Was helfen kann:**
- Schwangerschaftsgürtel/Bauchband
- Wärme (Wärmflasche, warmes Bad)
- Sanfte Massage
- Schwimmen
- Yoga und Dehnübungen
- Gute Matratze
- Flache Schuhe

**Im Alltag:**
- Nicht zu lange stehen oder sitzen
- Beim Heben in die Knie gehen
- Seitlich schlafen mit Kissen zwischen den Knien

**Zum Arzt wenn:**
- Schmerzen sehr stark sind
- Taubheitsgefühle auftreten
- Schmerzen ausstrahlen`,
  },
  {
    keywords: ['vorsorge', 'untersuchung', 'termin', 'frauenarzt', 'ctg', 'ultraschall'],
    response: `Regelmäßige Vorsorgeuntersuchungen sind wichtig für dich und dein Baby.

**Standard-Vorsorge-Schema:**
- Bis 32. SSW: alle 4 Wochen
- Ab 32. SSW: alle 2 Wochen
- Ab 36. SSW: wöchentlich

**Was wird untersucht?**
- Blutdruck und Gewicht
- Urin (Eiweiß, Zucker)
- Lage des Babys
- Herztöne (später CTG)
- Gebärmuttergröße

**Ultraschall (3x regulär):**
- 9.-12. SSW: Ersttrimester-Screening
- 19.-22. SSW: Großer Organ-Ultraschall
- 29.-32. SSW: Wachstum und Lage

**Tipp:** Notiere dir Fragen, die du beim nächsten Termin stellen möchtest!

Nutze unsere Kalender-Funktion, um alle Termine im Blick zu behalten.`,
  },
  {
    keywords: ['geburt', 'entbindung', 'kreißsaal', 'einleitung'],
    response: `Es ist gut, sich auf die Geburt vorzubereiten!

**Anzeichen für den Geburtsbeginn:**
- Regelmäßige Wehen (alle 5-10 Min.)
- Blasensprung (Fruchtwasser)
- Abgang des Schleimpfropfs
- Ziehen im unteren Rücken

**Wann ins Krankenhaus?**
- Regelmäßige Wehen alle 5 Minuten
- Bei Blasensprung
- Bei starken Blutungen → SOFORT

**Vorbereitung:**
- Geburtsvorbereitungskurs besuchen
- Kliniktasche packen (nutze unsere Checkliste!)
- Kreißsaal-Besichtigung
- Geburtsplan erstellen (falls gewünscht)

**Entbindungsmöglichkeiten:**
- Krankenhaus
- Geburtshaus
- Hausgeburt (bei unkomplizierter Schwangerschaft)

Sprich mit deiner Hebamme und Ärztin über deine Wünsche und Optionen!`,
  },
  {
    keywords: ['partner', 'mann', 'vater', 'papa', 'beziehung'],
    response: `Die Schwangerschaft verändert auch die Beziehung – und das ist ganz normal.

**Für Partner:**
- Sei geduldig und verständnisvoll
- Begleite sie zu Vorsorgeterminen
- Informiere dich aktiv über die Schwangerschaft
- Hilf im Haushalt
- Sei bei Geburtsvorbereitungskursen dabei

**Kommunikation:**
- Sprecht offen über Ängste und Erwartungen
- Plant gemeinsam für die Zeit nach der Geburt
- Verbringt bewusst Zeit zu zweit

**Intimität:**
Sex ist bei einer normalen Schwangerschaft unbedenklich. Kommuniziert offen darüber, was sich gut anfühlt.

**Tipp:** Nutzt die ParentPath Partner-Funktion, um gemeinsam die Schwangerschaft zu verfolgen und Namen zu swipen!`,
  },
];

// Mock responses based on keywords - English
const MOCK_RESPONSES_EN: MockResponse[] = [
  {
    keywords: ['nausea', 'nauseous', 'vomiting', 'morning sickness', 'sick'],
    response: `Nausea is very common, especially in the first trimester, affecting about 70-80% of all pregnant women. Here are some tips that can help:

**What you can try:**
- Small, frequent meals instead of fewer large ones
- Dry crackers or toast before getting up
- Ginger tea or ginger sweets
- Fresh air and exercise
- Avoid strong-smelling foods

**When to see a doctor:**
- If vomiting persists (more than 3 times daily)
- If you can't keep anything down
- If you experience weight loss or dehydration

The good news: For most women, nausea significantly improves after weeks 12-14.`,
  },
  {
    keywords: ['tired', 'fatigue', 'exhausted', 'energy', 'sleepy'],
    response: `Fatigue is one of the most common symptoms in pregnancy, especially in the first and third trimesters.

**Why are you so tired?**
Your body is doing something incredible! It's building the placenta, your blood volume increases by 50%, and your metabolism is working overtime.

**What can help:**
- Take rest breaks when possible
- Go to bed earlier
- Short power naps (20-30 min)
- Light exercise like walks
- Adequate iron through diet
- Drink plenty of water

**Tip:** Listen to your body! When it needs rest, that's an important signal.`,
  },
  {
    keywords: ['food', 'nutrition', 'diet', 'eat', 'can i eat', 'forbidden', 'allowed'],
    response: `A balanced diet is especially important during pregnancy.

**Recommended:**
- Plenty of fruits and vegetables (wash well!)
- Whole grain products
- Lean meat (well-cooked)
- Fish 1-2x per week (no predatory fish)
- Dairy products (pasteurised)
- Legumes and nuts

**Better to avoid:**
- Raw animal products (sushi, raw meat, tiramisu)
- Unpasteurised cheese
- Alcohol
- Too much caffeine (max. 200mg/day)
- Liver (too much vitamin A)

**Important:** Folic acid and possibly other supplements in consultation with your doctor.

Do you have a specific question about a food?`,
  },
  {
    keywords: ['sport', 'exercise', 'workout', 'fitness', 'yoga', 'swimming'],
    response: `Exercise during pregnancy is great and recommended!

**Well suited:**
- Swimming and aqua fitness
- Yoga (prenatal yoga)
- Walking
- Light strength training
- Cycling (carefully, balance!)
- Prenatal gymnastics

**Better to avoid:**
- Contact sports
- Sports with risk of falling
- Extreme exertion
- Exercises on your back (from 2nd trimester)
- Diving

**General tips:**
- Listen to your body
- Never exercise to exhaustion
- Follow the "talk test" rule
- Drink enough water
- Inform your trainer

If you were active before pregnancy, you can usually continue. Best to discuss this with your doctor.`,
  },
  {
    keywords: ['sleep', 'insomnia', 'falling asleep', 'sleeping', 'sleepless'],
    response: `Sleep problems are very common during pregnancy, especially in the third trimester.

**Common causes:**
- Frequent urination
- Uncomfortable sleeping position
- Heartburn
- Racing thoughts
- Baby movements

**What can help:**
- Side position (left is optimal for circulation)
- Pregnancy pillow between the knees
- Drink less in the evening
- Relaxation exercises before sleeping
- Regular sleep schedule
- Cool, dark bedroom

**For heartburn:**
- Elevate your head
- Last meal 2-3 hours before sleeping

Short naps during the day are perfectly fine and help compensate for the sleep deficit.`,
  },
  {
    keywords: ['back', 'back pain', 'lower back', 'sciatica'],
    response: `Back pain is unfortunately one of the most common complaints during pregnancy.

**Why does your back hurt?**
- Weight gain shifts your centre of gravity
- Hormones loosen ligaments and joints
- Changed posture

**What can help:**
- Pregnancy belt/belly band
- Heat (hot water bottle, warm bath)
- Gentle massage
- Swimming
- Yoga and stretching exercises
- Good mattress
- Flat shoes

**In daily life:**
- Don't stand or sit for too long
- Bend at the knees when lifting
- Sleep on your side with a pillow between your knees

**See a doctor if:**
- Pain is very severe
- Numbness occurs
- Pain radiates`,
  },
  {
    keywords: ['check-up', 'appointment', 'doctor', 'ultrasound', 'prenatal'],
    response: `Regular prenatal check-ups are important for you and your baby.

**Standard check-up schedule:**
- Until week 32: every 4 weeks
- From week 32: every 2 weeks
- From week 36: weekly

**What is examined?**
- Blood pressure and weight
- Urine (protein, sugar)
- Baby's position
- Heartbeat (later CTG)
- Uterus size

**Ultrasound (3x standard):**
- Weeks 9-12: First trimester screening
- Weeks 19-22: Detailed anatomy scan
- Weeks 29-32: Growth and position

**Tip:** Write down questions you want to ask at your next appointment!

Use our calendar feature to keep track of all appointments.`,
  },
  {
    keywords: ['birth', 'delivery', 'labour', 'labor', 'induction'],
    response: `It's good to prepare for the birth!

**Signs of labour starting:**
- Regular contractions (every 5-10 min)
- Water breaking (amniotic fluid)
- Loss of mucus plug
- Pulling sensation in the lower back

**When to go to the hospital?**
- Regular contractions every 5 minutes
- When your water breaks
- Heavy bleeding → IMMEDIATELY

**Preparation:**
- Attend a birth preparation class
- Pack your hospital bag (use our checklist!)
- Visit the delivery room
- Create a birth plan (if desired)

**Delivery options:**
- Hospital
- Birth centre
- Home birth (for uncomplicated pregnancies)

Talk to your midwife and doctor about your wishes and options!`,
  },
  {
    keywords: ['partner', 'husband', 'father', 'dad', 'relationship'],
    response: `Pregnancy also changes the relationship – and that's completely normal.

**For partners:**
- Be patient and understanding
- Accompany her to check-ups
- Actively learn about pregnancy
- Help with household tasks
- Attend birth preparation classes together

**Communication:**
- Talk openly about fears and expectations
- Plan together for life after birth
- Spend quality time together

**Intimacy:**
Sex during a normal pregnancy is safe. Communicate openly about what feels good.

**Tip:** Use the ParentPath partner feature to follow the pregnancy together and swipe baby names!`,
  },
];

// Default response when no keyword matches
const DEFAULT_RESPONSE_DE = `Danke für deine Frage!

Ich bin dein KI-Schwangerschaftsassistent und helfe dir gerne bei allgemeinen Fragen rund um die Schwangerschaft.

**Ich kann dir helfen bei Fragen zu:**
- Beschwerden wie Übelkeit, Müdigkeit, Rückenschmerzen
- Ernährung in der Schwangerschaft
- Sport und Bewegung
- Schlafproblemen
- Kindsbewegungen
- Vorsorgeuntersuchungen
- Geburtsvorbereitung
- und vielem mehr!

**Wichtig:** Ich ersetze keine ärztliche Beratung. Bei konkreten medizinischen Fragen wende dich bitte an deine Frauenärztin oder Hebamme.

Wie kann ich dir helfen?`;

const DEFAULT_RESPONSE_EN = `Thanks for your question!

I'm your AI pregnancy assistant and happy to help with general questions about pregnancy.

**I can help you with questions about:**
- Symptoms like nausea, fatigue, back pain
- Nutrition during pregnancy
- Exercise and fitness
- Sleep problems
- Baby movements
- Prenatal check-ups
- Birth preparation
- and much more!

**Important:** I do not replace medical advice. For specific medical questions, please contact your doctor or midwife.

How can I help you?`;

// Get mock response based on user message
export function getMockResponse(message: string, locale: 'de' | 'en' = 'de'): { response: string; isEmergency: boolean } {
  const lowerMessage = message.toLowerCase();

  // Check for emergency first (both languages)
  if (containsEmergencyKeyword(message)) {
    return {
      response: locale === 'en' ? EMERGENCY_RESPONSE_EN : EMERGENCY_RESPONSE_DE,
      isEmergency: true,
    };
  }

  // Select the right response set
  const responses = locale === 'en' ? MOCK_RESPONSES_EN : MOCK_RESPONSES_DE;

  // Find matching response
  for (const mockResponse of responses) {
    if (mockResponse.keywords.some((keyword) => lowerMessage.includes(keyword))) {
      return { response: mockResponse.response, isEmergency: false };
    }
  }

  // Return default response if no match
  return {
    response: locale === 'en' ? DEFAULT_RESPONSE_EN : DEFAULT_RESPONSE_DE,
    isEmergency: false,
  };
}

// Simulate typing delay (makes it feel more natural)
export function getTypingDelay(responseLength: number): number {
  // Base delay + variable delay based on response length
  // Minimum 1s, maximum 3s
  const baseDelay = 1000;
  const variableDelay = Math.min(responseLength * 5, 2000);
  return baseDelay + variableDelay;
}
